import {
  Collection,
  CollectionsApiCollectionsControllerGetCollectionRequest,
  CollectionsApiCollectionsControllerGetCuratedCollectionRequest,
} from '@givz/core-client';
import {
  QueryKey,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { curatedCollectionKey } from '../constants';
import { useCoreClientMutation, useCoreClientQuery } from './client';

export const collectionKeys = {
  base: ['collection'] as const,
  get: (params: CollectionsApiCollectionsControllerGetCollectionRequest) =>
    [...collectionKeys.base, 'get', params] as const,
  getCurated: (
    params: CollectionsApiCollectionsControllerGetCuratedCollectionRequest,
  ) => [...collectionKeys.base, 'getCurated', params] as const,
};

export const useCuratedCollection = (
  params: CollectionsApiCollectionsControllerGetCuratedCollectionRequest = {
    key: curatedCollectionKey,
  },
) => {
  return useCoreClientQuery({
    queryFn: (client) => (_queryContext) =>
      client.Collections.getCurated(params),
    queryKey: collectionKeys.getCurated(params),
    staleTime: Infinity,
  });
};

export const useGetCollection = (
  params: CollectionsApiCollectionsControllerGetCollectionRequest,
  options?: Partial<
    Pick<UseQueryOptions<unknown, unknown, unknown, QueryKey>, 'enabled'>
  >,
) => {
  return useCoreClientQuery({
    queryFn: (client) => (_queryContext) => client.Collections.get(params),
    queryKey: collectionKeys.get(params),
    staleTime: Infinity,
    ...options,
  });
};

export const useCreateHandpickedCollection = () => {
  const queryClient = useQueryClient();

  const onSuccess = (data: Collection) => {
    queryClient.invalidateQueries(collectionKeys.get({ gid: data.gid }));
  };

  return useCoreClientMutation({
    mutationFn: (client) => client.Collections.createHandpicked,
    onSuccess,
  });
};

export const useUpdateHandpickedCollection = () => {
  const queryClient = useQueryClient();

  const onSuccess = (data: Collection) => {
    queryClient.invalidateQueries(collectionKeys.get({ gid: data.gid }));
  };

  return useCoreClientMutation({
    mutationFn: (client) => client.Collections.updateHandpicked,
    onSuccess,
  });
};
